/**
 * D348/D349: the player-facing words for the shown world's theme, from `IdleContent.terms`
 * (30 keys, including the tab names) and its `currency` block.
 *
 * `setThemeContent` is called by `store.ts` once it knows the shown world's theme (an import
 * from `store.ts` back to here would cycle, since `view.ts` and `notation.ts` import this
 * file and `store.ts` imports them). Before that call, or for a module used on its own (a
 * unit test that never touches `store.ts`), every lookup here falls back to
 * `getIdleContent()`, the farm theme's own content: never blank, and no visible change for
 * the world while nothing has set a different theme.
 */
import { getIdleContent, type IdleContent } from '@hedgerow/data';

/** Every plain word, `decorationKinds` apart: that one is a record, not a single string. */
export type TermKey = Exclude<keyof IdleContent['terms'], 'decorationKinds'>;
export type Terms = Omit<IdleContent['terms'], 'decorationKinds'>;

let content: IdleContent | undefined;

/** Called once by `store.ts` after it builds `idleContent`; a test may call it again. */
export function setThemeContent(c: IdleContent): void {
  content = c;
}

function current(): IdleContent {
  return content ?? getIdleContent();
}

/** One term in the shown world's theme. */
export function t(key: TermKey): string {
  return current().terms[key];
}

/** The whole dictionary, for a screen that needs several terms at once. */
export function useTerms(): Terms {
  return current().terms;
}

/** The theme's money, short, after a number: `300c`, or a theme's own suffix. */
export function currencyShort(): string {
  return current().currency.short;
}

/** The theme's money, by name, plural: "coins", "tokens". */
export function currencyName(): string {
  return current().currency.name;
}

/** `world` -> `World`, for a term used at the start of a sentence or as a label. */
export function cap(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** D371: `Pass the world on` -> `pass the world on`, for a term that reads on mid-sentence. */
export function lower(s: string): string {
  return s.length ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/** D365: a building row's icon, from the shown world's theme, never the world's own barn. */
export function buildingIcon(): string {
  return current().theme.buildingIcon;
}
